import { action }                from 'mobx';
import { makeObservable }        from 'mobx';
import { observable }            from 'mobx';
import { runInAction }           from 'mobx';
import { computed }              from 'mobx';
import { Action }                from '@widesk-ui/components/ButtonActions';
import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import SettingsSidebar           from '@/TokamapEditor/ux/SettingsSidebar';
import PlanImportSidebar         from '@/TokamapEditor/ux/PlanImportSidebar';
import { SettingsSwatch }        from '@/TokamapEditor/ux/SettingsSidebar';
import ShapeSidebar              from '@/TokamapEditor/ux/ShapeSidebar';
import { ZONE_SIDEBAR_WIDTH }    from '@/TokamapEditor/ux/constants';
import HistoryListSidebar        from '@/TokamapEditor/ux/HistoryListSidebar';
import PlaceListSidebar          from '@/TokamapEditor/ux/PlaceListSidebar';
import PlaceTypeListSidebar      from '@/TokamapEditor/ux/PlaceTypeListSidebar';
import FieldListSidebar          from '@/TokamapEditor/ux/FieldListSidebar';
import ThemeListSidebar          from '@/TokamapEditor/ux/ThemeListSidebar';
import ThemeForm                 from '@/TokamapEditor/ux/ThemeForm';
import { ThemeTitle }            from '@/TokamapEditor/ux/ThemeForm';
import ThemeApplyForm            from '@/TokamapEditor/ux/ThemeApplyForm';
import { Theme }                 from '@/TokamapEditor/stores/themeStore';
import type { ThemeFieldTarget } from '@/TokamapEditor/helpers/themeImposed';
import themeStore                from '@/TokamapEditor/stores/themeStore';
import { message }               from '@widesk-ui/hooks/useMessage';
import messageWithUndo           from '@/TokamapEditor/helpers/messageWithUndo';
import ListStore                 from '@widesk-ui/stores/ListStore';
import PlaceModel                from '@models/map/PlaceModel';
import nextFreeLabel             from '@/tools/nextFreeLabel';
import { FormModal }             from '@widesk-ui/components/modal/FormModal';
import PlaceForm                 from '@forms/map/PlaceForm';
import { PlaceTitle }            from '@forms/map/PlaceForm';
import { PlaceHeaderImage }      from '@forms/map/PlaceForm';
import { UploadOutlined }        from '@ant-design/icons';
import ImportPlaceForm           from '@forms/map/ImportPlaceForm';
import PlaceTypeModel            from '@models/map/PlaceTypeModel';
import FieldModel                from '@models/map/FieldModel';
import { CopyOutlined }          from '@ant-design/icons';
import { ProfileOutlined }       from '@ant-design/icons';
import ProjectModel              from '@models/map/ProjectModel';
import ProjectPlaceTypeCopyForm  from '@forms/map/ProjectPlaceTypeCopyForm';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import { ViewProps }             from '@widesk-ui/components/View/viewTypes';
import PlacesWithoutShapeButton  from '@/TokamapEditor/ux/PlacesWithoutShapeButton';
import PlanZoneSidebar           from '@/TokamapEditor/ux/PlanZoneSidebar';
import type { PlanImportAdoption } from '@/TokamapEditor/ux/PlanImportSidebar';
import ShapesWithoutThemeButton  from '@/TokamapEditor/ux/ShapesWithoutThemeButton';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import framingStore              from '@/TokamapEditor/stores/framingStore';
import { withLockMark }          from '@/components/Functionality/withLockMark';
import { functionalityStore }    from '@/components/Functionality';
import { isBlockedFeature }      from '@/components/Functionality/functionalityGuards';
import unlinkShapesFromPlace     from '@/TokamapEditor/helpers/unlinkShapesFromPlace';

/** Durée de l'animation de sortie d'un `Drawer` d'antd. */
const CLOSE_ANIMATION_MS = 350;

const DEFAULT_OPTIONS: Options = {
	width: 400,
	title: '',
	render: () => null,
	renderTopLeft: () => null,
	renderTopRight: () => null,
	overlay: true,
};

export type Options = {
	reference?: string;
	featureKey?: string;
	largeTitle?: boolean;
	/** `ReactNode` et non `string` : certains écrans y placent un composant, comme le nom modifiable en
	 * place d'un thème (voir `openThemeForm`). */
	title: React.ReactNode;
	subTitle?: React.ReactNode;
	parentTitle?: string;
	closeButton?: boolean;
	bordered?: boolean;
	onBack?: () => void;
	onClose?: () => void;
	width: number;
	render: () => React.ReactNode;
	renderTopLeft: () => React.ReactNode;
	renderTopRight: () => React.ReactNode;
	overlay: boolean;
	actions?: Action[];
	footer?: React.ReactNode;
	padding?: [ViewProps['paddingV'], ViewProps['paddingH']];
	/** Interne : panneau en cours de fermeture, gardé dans la pile le temps de son animation de
	 * sortie. Sans ce sursis il disparaissait d'un coup, et le panneau du dessous restait décalé le
	 * temps de revenir — c'est le vide qu'on voyait à gauche. */
	closing?: boolean;
}
export type LowerOptions = Options & { upperPush?: boolean };
type ListStoreKey = 'places' | 'placeTypes' | 'fields' | 'themes' | 'history';

export class LeftSidebarStore {
	@observable listStores: Partial<Record<ListStoreKey, ListStore>> = {};

	/**
	 * Pile des panneaux ouverts, du premier plan (index 0) au plus profond.
	 *
	 * Une pile, et non deux emplacements nommés : la profondeur cesse d'être une question, le retour
	 * devient un `pop()` — plus de flèche câblée à la main qui saute un cran — et le fil d'Ariane se
	 * déduit des panneaux du dessous au lieu d'être recopié dans chaque `parentTitle`.
	 *
	 * `open`/`lowerOpen`/`options`/`lowerOptions` restent en façade au-dessus d'elle : ils désignent
	 * simplement les index 0 et 1.
	 */
	@observable.ref panels: Options[] = [];

	@observable opened = false;

	private _themeFormMounts = 0;

	// Point d'intérêt en cours de glisser-déposer depuis `PlaceListSidebar` vers une forme de la
	// carte — `null` en dehors d'un tel geste. Sert à la fois à masquer l'overlay sombre de la
	// sidebar le temps du geste (voir `LeftSidebar`, `mask`) et à transmettre le point d'intérêt visé
	// jusqu'au gestionnaire de dépose sur la carte, sans passer par `DataTransfer` (qui ne permet de
	// lire des données personnalisées qu'au moment du `drop`, jamais pendant le survol).
	@observable.ref draggingPlace: PlaceModel | null = null;

	// .ref : `options` contient des ReactNode/fonctions (footer, render...) et est toujours
	// remplacé en bloc (jamais muté champ par champ) — l'enhancer deep de MobX les proxifierait
	// sinon (y compris le `_store` interne de React), et une simple écriture React dessus
	// (validateChildKeys) déclencherait une réaction MobX en plein render.
	@computed get options(): Options {
		return this.panels.at(0) || DEFAULT_OPTIONS;
	}

	@computed get lowerOptions(): LowerOptions {
		return this.panels.at(1) || DEFAULT_OPTIONS;
	}

	/** Panneau du sommet — celui avec lequel l'utilisateur interagit. */
	@computed get topOptions(): Options {
		return this.panels.at(-1) || DEFAULT_OPTIONS;
	}

	@computed get lowerOpened() {
		return this.panels.length > 1;
	}

	constructor() {
		makeObservable(this);
	}

	/** Remplace toute la pile par ce seul panneau. */
	@action open(options?: Partial<Options>, opened = true) {
		// Prévient chaque contenu affiché qu'il est remplacé — pas seulement en cas de fermeture
		// explicite (croix/Échap) : un autre `open*()` (ex. la liste des points d'intérêts) remplace
		// aussi la pile sans jamais passer par `close()`.
		this.panels.forEach(panel => panel.onClose?.());

		this.panels = [{ ...DEFAULT_OPTIONS, ...options }];
		this.opened = opened;
	}

	/** Ouvre au DEUXIÈME niveau, en refermant ce qui était plus profond. */
	@action lowerOpen(lowerOptions?: Partial<LowerOptions>, lowerOpened = true) {
		this.panels.slice(1).forEach(panel => panel.onClose?.());

		this.panels = [this.options, ...(lowerOpened ? [{ ...DEFAULT_OPTIONS, ...lowerOptions }] : [])];
	}

	/**
	 * Empile un panneau par-dessus le sommet actuel, à n'importe quelle profondeur.
	 *
	 * C'est ce qui remplace les contournements employés jusqu'ici quand les deux niveaux étaient déjà
	 * pris : contenu masqué en `display: none` pour survivre, en-tête repris à la main, pied de tiroir
	 * disputé entre deux formulaires.
	 */
	@action push(options?: Partial<Options>) {
		this.panels = [...this.panels, { ...DEFAULT_OPTIONS, ...options }];
	}

	/** Dépile le panneau du sommet — c'est l'action « retour ». */
	@action pop() {
		if (this.panels.length <= 1) return this.close();

		this.panels.at(-1)?.onClose?.();
		this._retire(this.panels.length - 1);
	}

	/**
	 * Marque un panneau comme sortant, puis le retire une fois l'animation passée.
	 *
	 * Le laisser monté est ce qui permet au tiroir de jouer sa sortie ; le panneau du dessous cesse
	 * d'être poussé au même instant, et les deux mouvements se font ensemble.
	 */
	@action private _retire(from: number) {
		this.panels = this.panels.map((panel, i) => i >= from ? { ...panel, closing: true } : panel);

		setTimeout(() => runInAction(() => {
			this.panels = this.panels.filter(panel => !panel.closing);
		}), CLOSE_ANIMATION_MS);
	}

	@action setPanelOption<K extends keyof Options>(index: number, name: K, value: Options[K]) {
		const panel = this.panels.at(index);

		if (!panel) return;

		const at = index < 0 ? this.panels.length + index : index;

		this.panels = this.panels.map((p, i) => i === at ? { ...p, [name]: value } : p);
	}

	@action setOption<K extends keyof Options>(name: K, value: Options[K]) {
		this.setPanelOption(0, name, value);
	}

	@action setLowerOption<K extends keyof Options>(name: K, value: Options[K]) {
		this.setPanelOption(1, name, value);
	}

	/** Sur le panneau du sommet, quelle que soit sa profondeur. */
	@action setTopOption<K extends keyof Options>(name: K, value: Options[K]) {
		this.setPanelOption(-1, name, value);
	}

	@action setListStore(name: ListStoreKey, value: ListStore) {
		this.listStores = { ...this.listStores, [name]: value };
	}

	@action setDraggingPlace(place: PlaceModel | null) {
		this.draggingPlace = place;
	}

	public getListStore(name: ListStoreKey) {
		return this.listStores[name];
	}

	/**
	 * Referme toute la pile — en la laissant sortir.
	 *
	 * Vider `panels` sur-le-champ démontait le tiroir avant qu'il n'ait joué sa sortie : la sidebar
	 * disparaissait d'un coup. On passe donc par le même `_retire` que `pop`, qui marque les panneaux
	 * comme sortants et ne les retire qu'une fois l'animation passée.
	 */
	@action close() {
		if (!this.panels.length) {
			this.opened = false;
			return;
		}

		this.panels.forEach(panel => panel.onClose?.());
		this._retire(0);

		// `opened` ferme le rendu : il doit donc tomber APRÈS la sortie, pas pendant. Et seulement si
		// rien n'a rouvert entre-temps — le `setTimeout` de `_retire`, posé juste avant, a déjà vidé la
		// pile à cet instant, sauf si un `open()` l'a repeuplée.
		setTimeout(() => runInAction(() => {
			if (!this.panels.length) this.opened = false;
		}), CLOSE_ANIMATION_MS);
	}

	/** Referme tout ce qui est au-dessus du premier niveau. */
	@action closeLower() {
		this.panels.slice(1).forEach(panel => panel.onClose?.());
		this._retire(1);
	}

	openSettings() {
		this.open({
			reference: 'settings',
			title: 'Réglages',
			subTitle: currentProjectEditorStore.get.currentMap.label,
			renderTopLeft: () => <SettingsSwatch />,
			render: () => <SettingsSidebar />,
			closeButton: true,
			bordered: true,
			overlay: false,
			onClose: () => framingStore.setEditing(false),
		});

		framingStore.setEditing(true);
	}

	/**
	 * Panneau d'import de plan (DXF / SVG / PDF) — OUTIL DE TEST, cf.
	 * `PlanImportSidebar`. À remplacer par le parcours définitif.
	 */
	openPlanImport(options?: { adopt?: PlanImportAdoption }) {
		this.open({
			reference: 'plan-import',
			title: options?.adopt?.capture ? 'Analyser une zone' : 'Importer un plan',
			subTitle: currentProjectEditorStore.get.currentMap.label,
			render: () => <PlanImportSidebar adopt={options?.adopt} />,
			closeButton: true,
			bordered: true,
			width: 420,
		});
	}

	/**
	 * Panneau « zone d'analyse » — l'autre entrée du même parcours (voir `PlanZoneSidebar`) :
	 * on entoure une partie de la carte au lieu de déposer un fichier. Il passe la main à
	 * `openPlanImport({ adopt })` dès l'import créé.
	 */
	openPlanZone() {
		this.open({
			reference: 'plan-zone',
			title: 'Analyser une zone',
			subTitle: currentProjectEditorStore.get.currentMap.label,
			render: () => <PlanZoneSidebar />,
			closeButton: true,
			bordered: true,
			width: 420,
			// La zone se trace SUR la carte : l'overlay sombre du tiroir capte les clics et
			// refermerait le panneau au premier geste — l'outil, lui, resterait armé (le tiroir ne
			// démonte pas son contenu en se fermant), d'où un rectangle qui se trace sans qu'aucun
			// panneau ne réagisse. Même raison que `PlanImportSidebar` pendant le placement du fond,
			// à ceci près qu'ici c'est vrai dès la première frame : ce panneau n'existe que pour ça.
			overlay: false,
		});
	}

	openFeatureSettings(open = true, featureKey?: string) {
		const current = this.panels.at(0);

		if (
			open
			&& featureKey
			&& this.opened
			&& current?.reference === 'feature_settings'
			&& current.featureKey === featureKey
			&& !current.closing
		) return;

		this.open({
			reference: 'feature_settings',
			featureKey,
			closeButton: true,
			bordered: true,
			largeTitle: true,
			render: () => <ShapeSidebar />,
			width: ZONE_SIDEBAR_WIDTH,
			overlay: false,
			// Fermeture (croix, Échap, ouverture d'une autre sidebar par-dessus...) sans repasser
			// par un clic sur la carte : sinon la forme reste visuellement sélectionnée dans
			// l'éditeur (halo de focus) alors que sa sidebar a disparu.
			onClose: () => currentEditorStore.clearSelection(),
		}, open);
	}

	openPlaces() {
		this.open({
			title: PlaceModel.localize('plural'),
			subTitle: (
				<View row>
					<PlacesWithoutShapeButton />
				</View>
			),
			// L'ajout est un bouton en bas de la liste (voir `PlaceListSidebar`), comme pour les types et
			// les thèmes. Le menu `…` ne garde que l'import.
			actions: [
				{
					icon: <UploadOutlined />,
					label: `Importer des points d'intérêts`,
					renderWrapper: withLockMark({ reference: 'place_import' }),
					onClick: async () => {
						if (await isBlockedFeature('place_import')) return;

						this.lowerOpen({
							title: `Importer des points d'intérêts`,
							parentTitle: PlaceModel.localize('plural'),
							closeButton: true,
							render: () => <ImportPlaceForm project={currentProjectEditorStore.get.project} />,
						});
					},
				},
			],
			reference: 'places',
			render: () => <PlaceListSidebar />,
			closeButton: true,
		});
	}

	/** Formulaire d'ajout d'un point d'intérêt, appelé par le bouton en bas de `PlaceListSidebar`. */
	/**
	 * Ouvre la fiche d'un point d'intérêt au deuxième niveau.
	 *
	 * Dans le store et non dans la liste qui l'affichait : la sidebar d'une zone y mène aussi, et deux
	 * copies de cet en-tête auraient fini par diverger.
	 */
	openPlace(model: PlaceModel) {
		this.lowerOpen({
			closeButton: true,
			parentTitle: PlaceModel.localize('plural'),
			// Titre et vignette posés dès l'ouverture, remplacés au montage par ceux du formulaire (les
			// mêmes composants, avec son indicateur d'enregistrement) : sans ça le panneau s'ouvrirait
			// sans en-tête le temps d'un rendu, puis changerait sous les yeux de l'utilisateur.
			title: <PlaceTitle model={model} />,
			renderTopLeft: () => <PlaceHeaderImage model={model} />,
			// `inSidebar` : modification au fil de l'eau, sans bouton.
			render: () => <PlaceForm inSidebar model={model} projectUrn={currentProjectEditorStore.get.project.urn} />,
		});
	}

	/**
	 * Crée un point d'intérêt sous un nom généré et ouvre directement sa fiche, comme pour un thème.
	 *
	 * `onCreated` sert aux appelants qui ont quelque chose à faire du point d'intérêt une fois créé —
	 * la sidebar d'une zone, qui le relie à la forme sélectionnée.
	 */
	async openNewPlace(onCreated?: (place: PlaceModel) => void, initialLabel?: string) {
		const taken = ((this.getListStore('places')?.data || []) as PlaceModel[])
			.map(place => place.label)
			.filter(Boolean);

		const label = initialLabel || nextFreeLabel(PlaceModel.localize(), taken, { numberFirst: true });
		const model = new PlaceModel();

		try {
			await model.patch({ label, project: currentProjectEditorStore.get.project.urn });
		} catch (err) {
			message.exception(err);
			return;
		}

		this.getListStore('places')?.reload();
		functionalityStore.refreshUsagesOf('place_limit');
		onCreated?.(model);

		this.openPlace(model);

		messageWithUndo(<span>« {model.label} » a été créé.</span>, async () => {
			await unlinkShapesFromPlace(model);
			await model.destroy();

			this.getListStore('places')?.reload();
			functionalityStore.refreshUsagesOf('place_limit');
			this.pop();
		});
	}

	openPlaceTypes() {
		this.open({
			title: PlaceTypeModel.localize('plural'),
			// Le même raccourci que dans la liste des points d'intérêts : cette liste-ci parle de leurs
			// types, elle regarde donc le même plan et les mêmes zones orphelines. Deux écrans voisins
			// qui n'offrent pas la même sortie sur la carte se lisent comme un oubli.
			subTitle: (
				<View row>
					<PlacesWithoutShapeButton />
				</View>
			),
			// Pas de `footer` ici : `List` y place sa pagination, mais seulement si l'emplacement est
			// libre (voir son `setFooter`). Le bouton d'ajout est donc ancré en bas de la liste
			// elle-même, dans `PlaceTypeListSidebar`.
			actions: [
				{
					icon: <ProfileOutlined />,
					label: `Gérer les ${FieldModel.localize('plural', 'l')} du projet`,
					onClick: () => this.openFields(),
				},
				{
					icon: <CopyOutlined />,
					label: `Copier les types entre ${ProjectModel.localize('plural', 'l')}`,
					onClick: () => {
						FormModal.open({
							title: `Copier les ${PlaceTypeModel.localize('plural', 'l')} entre ${ProjectModel.localize('plural', 'l')}`,
							children: (
								<ProjectPlaceTypeCopyForm
									projectUrn={currentProjectEditorStore.get.project.urn}
									onSuccess={() => this.getListStore('placeTypes')?.reload()}
								/>
							),
							okButtonProps: { text: 'Copier' },
						});
					},
				},
			],
			reference: 'place_types',
			render: () => <PlaceTypeListSidebar />,
			closeButton: true,
		});
	}

	/**
	 * Liste globale des champs du projet. Elle n'a plus d'entrée dans la barre du bas : un champ ne
	 * sert que rattaché à un type (voir `PlaceTypeFieldModel`), donc le chemin normal passe par le
	 * type. Elle reste accessible depuis `openPlaceTypes` pour le seul besoin qui la justifie —
	 * réutiliser ou renommer un champ partagé par plusieurs types — d'où la flèche de retour.
	 */
	openFields() {
		this.open({
			onBack: () => this.openPlaceTypes(),
			parentTitle: PlaceTypeModel.localize('plural'),
			title: FieldModel.localize('plural'),
			// Pas d'ajout ici : un champ se crée depuis le type auquel on veut l'attacher (voir
			// `FieldNaturePicker`). En créer un depuis cette liste produirait un champ orphelin, que rien
			// n'utilise et que l'écran ne permet pas de rattacher.
			reference: 'fields',
			render: () => <FieldListSidebar />,
			closeButton: true,
		});
	}

	openThemes() {
		this.open({
			title: 'Thèmes',
			// Même place que le compte de zones non liées dans la liste des points d'intérêts : une
			// information de couverture, qui porte sur l'ensemble et non sur une ligne.
			subTitle: (
				<View row>
					<ShapesWithoutThemeButton />
				</View>
			),
			// L'ajout est un bouton en bas de la liste (voir `ThemeListSidebar`), comme pour les types.
			reference: 'themes',
			render: () => <ThemeListSidebar />,
			closeButton: true,
		});
	}

	/**
	 * Crée un thème au nom généré et ouvre directement sa modification : plus d'étape intermédiaire
	 * pour saisir le nom, qui reste éditable dans cette même sidebar.
	 */
	async addAndEditTheme() {
		try {
			const theme = await themeStore.addTheme();

			// La liste vient de l'API : un thème CRÉÉ n'y apparaît qu'au rechargement. Les modifications
			// suivantes, elles, se rendent depuis `themeStore` sans recharger.
			void this.getListStore('themes')?.reload();

			this.openThemeForm(theme);

			// Le nom étant généré, on le cite : c'est la seule façon pour l'utilisateur de savoir sous
			// quel nom le thème vient d'apparaître dans la liste.
			messageWithUndo(<span>Le thème « {theme.name} » a été créé.</span>, async () => {
				await themeStore.removeTheme(theme.id);

				this.pop();
				await this.getListStore('themes')?.reload();
			});
		} catch (err) {
			message.exception(err);
		}
	}

	/**
	 * Sous-sidebar de modification d'un thème, depuis leur liste ou depuis une forme. `focus` : le
	 * réglage à montrer dès l'ouverture.
	 */
	openThemeForm(theme: Theme, { fromShape = false, focus }: { fromShape?: boolean; focus?: ThemeFieldTarget; } = {}) {
		const mount = ++this._themeFormMounts;

		this.lowerOpen({
			closeButton: true,
			parentTitle: fromShape ? undefined : 'Thèmes',
			// Le nom se modifie directement dans le titre — voir `ThemeTitle`.
			title: <ThemeTitle themeId={theme.id} />,
			// `ThemeForm` gère son propre padding : ses onglets Apparence/Texte doivent aller d'un bord
			// à l'autre (leur trait de séparation en dépend), comme ceux de `AreaSidebar`.
			padding: [0, 0],
			render: () => <ThemeForm key={mount} themeId={theme.id} focus={focus} />,
		});
	}

	/** Ouvre le thème sur le réglage qu'il impose à la forme sélectionnée. */
	openThemeField(target: ThemeFieldTarget | undefined) {
		const theme = target && themeStore.getTheme(target.themeId);

		if (theme) this.openThemeForm(theme, { fromShape: true, focus: target });
	}

	/** Sous-sidebar d'application en lot d'un thème — voir `ThemeApplyForm`. */
	openThemeApply(theme: Theme) {
		this.lowerOpen({
			closeButton: true,
			parentTitle: 'Thèmes',
			title: `Appliquer "${theme.name}"`,
			render: () => <ThemeApplyForm theme={theme} />,
		});
	}

	openHistory() {
		this.open({
			title: 'Historique des publications',
			// Le projet et non la carte ouverte : on publie le projet entier, l'historique aussi.
			subTitle: currentProjectEditorStore.get.project.title,
			reference: 'history',
			render: () => <HistoryListSidebar />,
			closeButton: true,
		});
	}
}

export default new LeftSidebarStore();